import Api from "../utils/Api";

import alt from "../alt";
import SessionActions from "../actions/SessionActions";
import { useDevicePermissionsStore } from "../storez/DevicePermissionsStore";
import { useDateRangeFilterStore } from "../storez/DateRangeFilterStore";
import { useSessionStore } from "../storez/SessionStore";
import { initGoogleTagManager } from "../utils/googleTagManagerUtils";
import { useUserflowStore } from "../storez/UserflowStore";
import { loginRedirectUser } from "../utils/api/Users";
import { resetPostHog } from "../utils/PostHogUtils";

const clearLocalStorage = () => {
  localStorage.removeItem("DEVICEID");
  localStorage.removeItem("DEVICE_NAME");
  localStorage.removeItem("USERNAME");
  localStorage.removeItem("USERID");
  localStorage.removeItem("TOKEN");
  localStorage.removeItem("SUPPORTADMIN");
  localStorage.removeItem("REMOTE_SUPPORT_ADMIN");
  localStorage.removeItem("DEVICE_REGION");
  localStorage.removeItem("DEVICE_TIMEZONE");
  localStorage.removeItem("DEVICE_HUBSPOT_COMPANY_ID");
  localStorage.setItem("manuallySetRunningSyncs", "{}");
};

class SessionStore {
  constructor() {
    this.selected_device = localStorage.getItem("DEVICEID");
    this.selected_device_name = localStorage.getItem("DEVICE_NAME");
    this.selected_device_region = localStorage.getItem("DEVICE_REGION");
    this.username = localStorage.getItem("USERNAME");
    this.user_id = localStorage.getItem("USERID");
    this.token = localStorage.getItem("TOKEN");
    this.support_admin = localStorage.getItem("SUPPORTADMIN") === "true";
    this.remote_support_admin = localStorage.getItem("REMOTE_SUPPORT_ADMIN") === "true";
    this.hubspot_company_id = localStorage.getItem("DEVICE_HUBSPOT_COMPANY_ID");
    this.available_devices = [];
    this.login_failed = false;
    this.login_failed_reason = "";
    this.available_devices_loaded = false;
    this.googleTagManagerInitialised = false;

    if (this.support_admin) {
      useDevicePermissionsStore.setState({ supportAdmin: this.support_admin });
    }

    const sessionState = {
      user: { id: this.user_id, username: this.username },
      token: this.token,
      supportAdmin: this.support_admin,
      remoteSupportAdmin: this.remote_support_admin,
    };

    if (this.selected_device) {
      sessionState.device = {
        id: this.selected_device,
        name: this.selected_device_name,
        region: this.selected_device_region,
        timezone: localStorage.getItem("DEVICE_TIMEZONE"),
      };
      this.initGoogleTagManagerIfRequired(this.selected_device_region);
    }

    useSessionStore.setState(sessionState);

    this.exportPublicMethods({
      getSelectedDevice: this.getSelectedDevice,
      getSelectedDeviceName: this.getSelectedDeviceName,
      getSelectedRegion: this.getSelectedRegion,
      getAvailableDevices: this.getAvailableDevices,
      getAvailableDevicesLoaded: this.getAvailableDevicesLoaded,
      getUsername: this.getUsername,
      hasDevice: this.hasDevice,
      isAuthenticated: this.isAuthenticated,
      getToken: this.getToken,
      getUserId: this.getUserId,
      failedLogin: this.failedLogin,
      resetLoginFailure: this.resetLoginFailure,
      failedLoginReason: this.failedLoginReason,
      getDevicePermissions: this.getDevicePermissions,
      isSupportAdmin: this.isSupportAdmin,
      hasDevicePermissionsLoaded: this.hasDevicePermissionsLoaded,
    });

    this.bindListeners({
      handleChangeDevice: SessionActions.CHANGE_DEVICE,
      handleFetchAvailableDevices: SessionActions.FETCH_AVAILABLE_DEVICES,
      handleFetchAvailableDevicesUpdated: SessionActions.FETCH_AVAILABLE_DEVICES_UPDATED,

      handleAuthenticate: SessionActions.AUTHENTICATE,
      handleAuthenticateSuccess: SessionActions.AUTHENTICATE_SUCCESS,
      handleAuthenticateFailure: SessionActions.AUTHENTICATE_FAILURE,
      handleLogout: SessionActions.LOGOUT,

      handleDeselectDevice: SessionActions.DESELECT_DEVICE,
      handleInvalidSessionError: SessionActions.INVALID_SESSION_ERROR,
    });
  }

  /**
   * @deprecated Use {@link useSessionStore} instead.
   */
  getSelectedDevice = () => {
    return this.selected_device;
  };

  /**
   * @deprecated Use {@link useSessionStore} instead.
   */
  getSelectedRegion = () => {
    return this.selected_device_region;
  };

  /**
   * @deprecated Use {@link useSessionStore} instead.
   */
  getSelectedDeviceName = () => {
    return this.selected_device_name;
  };

  getAvailableDevices = () => {
    return this.available_devices;
  };

  /**
   * @deprecated Use {@link useSessionStore} instead.
   */
  getUsername = () => {
    return this.username;
  };

  getAvailableDevicesLoaded = () => {
    return this.available_devices_loaded;
  };

  /**
   * @deprecated Use {@link useSessionStore} instead.
   */
  hasDevice = () => {
    return !!this.selected_device;
  };

  /**
   * @deprecated Use {@link useSessionStore} instead.
   */
  isAuthenticated = () => {
    return !!this.token;
  };

  /**
   * @deprecated Use {@link useSessionStore} instead.
   */
  getToken = () => {
    return this.token;
  };

  getUserId = () => {
    return this.user_id;
  };

  /**
   * @deprecated Use {@link useDevicePermissionsStore} instead.
   */
  getDevicePermissions = () => {
    return useDevicePermissionsStore.getState().permissions;
  };

  /**
   * @deprecated Use {@link useDevicePermissionsStore} instead.
   */
  hasDevicePermissionsLoaded = () => {
    return useDevicePermissionsStore.getState().loaded;
  };

  /**
   * @deprecated Use {@link useSessionStore} or {@link useDevicePermissionsStore} instead.
   */
  isSupportAdmin = () => {
    return this.support_admin === true;
  };

  handleChangeDevice = (device) => {
    this.selected_device = device.deviceid;
    localStorage.setItem("DEVICEID", device.deviceid);
    this.selected_device_name = device.device.user_defined_name;
    localStorage.setItem("DEVICE_NAME", device.device.user_defined_name);
    this.selected_device_region = device.device.region;
    localStorage.setItem("DEVICE_REGION", device.device.region);
    localStorage.setItem("DEVICE_TIMEZONE", device.device.timezone);

    this.hubspot_company_id = device.device.hubspot_company_id;
    localStorage.setItem("DEVICE_HUBSPOT_COMPANY_ID", this.hubspot_company_id);

    this.initGoogleTagManagerIfRequired(this.selected_device_region);

    useSessionStore.setState({
      device: {
        id: this.selected_device,
        region: this.selected_device_region,
        name: this.selected_device_name,
        timezone: device.device.timezone,
      },
    });

    setTimeout(() => {
      useUserflowStore.getState().verifyAuth();
    }, 0);
  };

  handleFetchAvailableDevices = () => {
    if (this.available_devices.length === 0) {
      Api.get("/ajax/devices", function (result) {
        setTimeout(() => SessionActions.fetchAvailableDevicesUpdated(result), 0);
      });
    }
  };

  handleFetchAvailableDevicesUpdated = (result) => {
    this.available_devices_loaded = true;
    this.available_devices = result["devices"];
  };

  handleAuthenticate = (creds) => {
    this.username = undefined;
    this.token = undefined;
    this.support_admin = false;
    this.remote_support_admin = false;
    this.available_devices = [];
    this.available_devices_loaded = false;

    clearLocalStorage();

    Api.unauthenticated_post(
      "/authenticate",
      creds,
      function (data) {
        SessionActions.authenticateSuccess(data);
      },
      function (data) {
        SessionActions.authenticateFailure(data);
      }
    );
  };

  handleAuthenticateSuccess = (data) => {
    useDateRangeFilterStore.getState().reset();
    clearLocalStorage();
    localStorage.setItem("USERNAME", data.username);
    localStorage.setItem("USERID", data.id);
    localStorage.setItem("TOKEN", data.token);
    if (data.support_admin) {
      localStorage.setItem("SUPPORTADMIN", data.support_admin);
      this.support_admin = data.support_admin;
      useDevicePermissionsStore.setState({ supportAdmin: this.support_admin });
    }

    localStorage.setItem("REMOTE_SUPPORT_ADMIN", data.remote_support_admin);
    this.remote_support_admin = data.remote_support_admin;

    this.username = data.username;
    this.token = data.token;
    this.user_id = data.id;
    this.login_failed = false;
    this.login_failed_reason = "";

    useSessionStore.setState({
      user: { id: this.user_id, username: this.username },
      token: this.token,
      supportAdmin: !!this.support_admin,
      remoteSupportAdmin: !!this.remote_support_admin,
    });
  };

  handleAuthenticateFailure = (data) => {
    clearLocalStorage();

    this.login_failed = true;
    this.login_failed_reason = data["error"];
  };

  resetLoginFailure = () => {
    this.login_failed = false;
    this.login_failed_reason = "";
  };

  handleInvalidSessionError() {
    this.handleLogout(false);
  }

  handleLogout(logoutQAuth = true) {
    void Api.postAsync(`/auth/v1/logout`)
      .finally(() => this.clearSessionInfo())
      .finally(() => loginRedirectUser(logoutQAuth));
  }

  clearSessionInfo() {
    clearLocalStorage();
    this.username = undefined;
    this.token = undefined;
    this.user_id = undefined;
    this.selected_device = undefined;
    this.selected_device_name = undefined;
    this.selected_device_region = undefined;
    this.hubspot_company_id = undefined;
    this.available_devices = [];
    this.available_devices_loaded = false;
    this.login_failed = false;
    this.login_failed_reason = "";
    this.support_admin = false;
    this.remote_support_admin = false;
    this.deviceId = undefined;

    useSessionStore.getState().reset();
    useDevicePermissionsStore.getState().reset();
    useUserflowStore.getState().resetUserflow();

    resetPostHog();
  }

  handleDeselectDevice = () => {
    this.selected_device = undefined;
    localStorage.removeItem("DEVICEID");

    this.selected_device_name = undefined;
    localStorage.removeItem("DEVICE_NAME");

    this.selected_device_region = undefined;
    localStorage.removeItem("DEVICE_REGION");

    localStorage.removeItem("DEVICE_TIMEZONE");

    this.hubspot_company_id = undefined;
    localStorage.removeItem("DEVICE_HUBSPOT_COMPANY_ID");

    useSessionStore.setState({ device: undefined });
  };

  failedLogin = () => {
    return this.login_failed;
  };

  failedLoginReason = () => {
    return this.login_failed_reason;
  };

  initGoogleTagManagerIfRequired = (applianceRegion) => {
    if (process.env.NODE_ENV !== "production") {
      return;
    }

    if (this.googleTagManagerInitialised) {
      return;
    }

    initGoogleTagManager(applianceRegion);
    this.googleTagManagerInitialised = true;
  };
}

/**
 * @deprecated Use the Zustand {@link useSessionStore} (`client/src/js/storez/SessionStore.ts`) instead.
 *
 * Eg, do:
 * ```ts
 * const appliance = useSessionStore().getState().getDevice().id;
 * ```
 * or
 * ```ts
 * const [appliance] = useSessionStore((state) => [state.device?.id] as const);
 * ```
 * instead of
 * ```ts
 * const appliance = SessionStore.getSelectedDevice()
 * ```
 */
export default alt.createStore(SessionStore, "SessionStore");
